<template>
    <div>
        <form-wrapper class="form__fields" :isSubmittable="false">
            <div class="auth-token-form">
                <text-field
                    v-if="showNpiField"
                    :label="$t('pages.auth.verify_token.form.npi_number.label')"
                    :placeholder="$t('pages.auth.verify_token.form.npi_number.placeholder')"
                    :isRequired="true"
                    :hasAutofocus="true"
                    :formData="data"
                    :isDisabled="isLoading"
                    :error="npiErrorMessage"
                    valuePath="npiNumber"
                    @change="change($event)"
                ></text-field>

                <text-field
                    v-if="showTokenField"
                    :label="$t('pages.auth.verify_token.form.token.label')"
                    :placeholder="$t('pages.auth.verify_token.form.token.placeholder')"
                    :isRequired="true"
                    :hasAutofocus="!showNpiField"
                    :formData="data"
                    :isDisabled="isLoading || isProbing"
                    :error="tokenErrorMessage"
                    valuePath="token"
                    @change="change($event)"
                ></text-field>

                <common-buttons v-if="showVerifyButton" class="verify-buttons">
                    <common-button
                        :label="$t('pages.auth.verify_token.form.verify')"
                        :isProcessing="isLoading || isProbing"
                        type="primary"
                        size="small"
                        @click="verifyToken"
                    ></common-button>
                </common-buttons>
            </div>
        </form-wrapper>

        <verify-email-modal
            :isOpen="isEmailModalOpen"
            :npiNumber="data.npiNumber"
            :token="data.token"
            :closeOnClickAway="false"
            @close="isEmailModalOpen = false"
            @email-submitted="handleEmailSubmitted"
        ></verify-email-modal>

        <verify-code-modal
            :isOpen="isCodeModalOpen"
            :email="userEmail"
            :accessLogId="externalProviderReferralsAccessLogId"
            :closeOnClickAway="false"
            @close="isCodeModalOpen = false"
            @code-verified="handleCodeVerified"
        ></verify-code-modal>

        <referral-info-modal
            :isOpen="isReferralInfoModalOpen"
            :referral="referral"
            :referralPoolId="referral && referral.id ? String(referral.id) : null"
            :npiNumber="data.npiNumber || null"
            :token="data.token"
            :closeOnClickAway="false"
            @close="isReferralInfoModalOpen = false"
        ></referral-info-modal>
    </div>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import VerifyEmailModal from 'components/page-elements/auth/VerifyEmailModal'
    import VerifyCodeModal from 'components/page-elements/auth/VerifyCodeModal'
    import ReferralInfoModal from 'components/page-elements/auth/ReferralInfoModal'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'

    export default {
        mixins: [FormMixin],

        components: {
            VerifyEmailModal,
            VerifyCodeModal,
            ReferralInfoModal,
        },

        data() {
            return {
                isLoading: false,
                isProbing: false,
                npiErrorMessage: '',
                tokenErrorMessage: '',
                data: {
                    npiNumber: '',
                    token: '',
                },
                requiresNpi: false,
                isEmailModalOpen: false,
                isCodeModalOpen: false,
                isReferralInfoModalOpen: false,
                userEmail: '',
                externalProviderReferralsAccessLogId: null,
                referral: null,
            }
        },

        computed: {
            hasQueryToken() {
                return Boolean(this.$route.query.token)
            },

            showNpiField() {
                if (this.hasQueryToken) {
                    return this.requiresNpi
                }
                return true
            },

            showTokenField() {
                if (this.hasQueryToken) {
                    return Boolean(this.tokenErrorMessage) && !this.requiresNpi
                }
                return true
            },

            showVerifyButton() {
                if (this.isProbing) return false
                return this.showNpiField || this.showTokenField
            },
        },

        methods: {
            emitPageStatus() {
                let status = 'manual'
                if (this.isProbing) status = 'verifying'
                else if (this.requiresNpi) status = 'needs_npi'
                else if (this.hasQueryToken) status = 'token_only'
                this.$emit('status-change', status)
            },

            handleAccessSuccess(response) {
                if (response.requiresNpi) {
                    this.requiresNpi = true
                    this.isLoading = false
                    this.isProbing = false
                    this.emitPageStatus()
                    return
                }

                if (response.emailRegistered || response.contactKnown) {
                    ReferralsPoolAPI.verifyWithToken(response.token, response.externalProviderReferralsAccessLogId)
                        .then((referral) => {
                            this.isReferralInfoModalOpen = true
                            this.referral = referral
                        })
                        .catch(() => {
                            this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_invalid')
                        })
                        .finally(() => {
                            this.isLoading = false
                            this.isProbing = false
                            this.emitPageStatus()
                        })
                    return
                }

                this.isLoading = false
                this.isProbing = false
                this.isEmailModalOpen = true
                this.emitPageStatus()
            },

            handleAccessError(error) {
                this.error = error.response
                this.isLoading = false
                this.isProbing = false

                const firstError = this.error && this.error.data && this.error.data[0]
                if (firstError && firstError.field === 'npiNumber') {
                    this.npiErrorMessage = this.$t('pages.auth.verify_token.errors.npi_mismatch')
                } else if (firstError && firstError.field === 'token' && firstError.code === 'entity.expired') {
                    this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_expired')
                } else if (firstError && firstError.field === 'token' && firstError.code === 'entity.invalid') {
                    this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_invalid')
                } else if (this.hasQueryToken) {
                    this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_invalid_token_only')
                } else {
                    this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_invalid_common')
                }
                this.emitPageStatus()
            },

            probeToken() {
                this.tokenErrorMessage = ''
                this.npiErrorMessage = ''
                this.isProbing = true
                this.emitPageStatus()

                ReferralsPoolAPI.accessWithToken(undefined, this.data.token.trim())
                    .then((response) => this.handleAccessSuccess(response))
                    .catch((error) => this.handleAccessError(error))
            },

            verifyToken() {
                this.npiErrorMessage = ''
                this.tokenErrorMessage = ''

                if (this.showNpiField && this.data.npiNumber.length !== 10) {
                    this.npiErrorMessage = this.$t('pages.auth.verify_token.errors.npi_must_be_10_digits')
                    return
                }

                if (!this.data.token) {
                    this.tokenErrorMessage = this.$t('pages.auth.verify_token.errors.token_required')
                    return
                }

                this.isLoading = true

                const npiNumber = this.showNpiField ? this.data.npiNumber : undefined

                ReferralsPoolAPI.accessWithToken(npiNumber, this.data.token.trim())
                    .then((response) => this.handleAccessSuccess(response))
                    .catch((error) => this.handleAccessError(error))
            },

            handleEmailSubmitted(payload) {
                this.userEmail = payload.email
                this.externalProviderReferralsAccessLogId = payload.externalProviderReferralsAccessLogId
                this.isEmailModalOpen = false
                this.isCodeModalOpen = true
            },

            handleCodeVerified(referral) {
                this.isCodeModalOpen = false
                this.isReferralInfoModalOpen = true
                this.referral = referral
            },
        },

        mounted() {
            if (this.$route.query.token) {
                this.data.token = this.$route.query.token
                this.probeToken()
            } else {
                this.emitPageStatus()
            }
        },
    }
</script>

<style scoped lang="scss">
    .auth-token-form {
        display: flex;
        flex-direction: column;
        gap: 18px;
    }

    .verify-buttons {
        margin-top: 5px;
    }
</style>
